import React, { useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

function RestrictedDateSelector({ selectedDate, onDateChange, showCalendar, onToggleCalendar }) {
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  const monthNames = [
    'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
    'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
  ];

  const dayNames = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

  // Calculate date range: 3 days in past, today, 2 days in future
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const minDate = new Date(today);
  minDate.setDate(minDate.getDate() - 3);
  
  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + 2);

  function formatDate(isoDate) {
    if (!isoDate) return '';
    const [year, month, day] = isoDate.split('-');
    return `${day}/${month}/${year}`;
  }

  function getDaysInMonth(month, year) {
    return new Date(year, month + 1, 0).getDate();
  }

  function getFirstDayOfMonth(month, year) {
    return new Date(year, month, 1).getDay();
  }

  function isDateInRange(day, month, year) {
    const date = new Date(year, month, day);
    date.setHours(0, 0, 0, 0);
    return date >= minDate && date <= maxDate;
  }

  function isToday(day, month, year) {
    const date = new Date(year, month, day);
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }

  function generateCalendarDays() {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  }

  function handleDateSelect(day) {
    if (!day) return;
    if (!isDateInRange(day, currentMonth, currentYear)) return;

    const year = currentYear;
    const month = String(currentMonth + 1).padStart(2, '0');
    const dayPadded = String(day).padStart(2, '0');
    const formattedDate = `${year}-${month}-${dayPadded}`;
    
    onDateChange(formattedDate);
    onToggleCalendar();
  }

  function isSelectedDate(day) {
    if (!day || !selectedDate) return false;
    const [year, month, dayOfMonth] = selectedDate.split('-').map(Number);
    return (
      dayOfMonth === day &&
      (month - 1) === currentMonth &&
      year === currentYear
    );
  }

  function goToPreviousMonth() {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  }

  function goToNextMonth() {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  }

  const calendarDays = generateCalendarDays();

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 sm:p-4">
      <div className="relative">
        <label className="flex items-center gap-2 mb-2 text-xs font-semibold text-slate-800 font-montserrat">
          <Calendar className="w-3.5 h-3.5 text-brand-primary" />
          Fecha de Resumen
        </label>
        
        <button
          type="button"
          onClick={onToggleCalendar}
          className="w-full rounded-lg border-2 border-slate-300 bg-white px-3 py-2.5 text-sm text-left focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all flex items-center justify-between font-montserrat hover:border-brand-primary"
        >
          <span className="text-slate-800 font-medium">
            {selectedDate ? formatDate(selectedDate) : 'Selecciona una fecha'}
          </span>
          <Calendar className="w-4 h-4 text-slate-400" />
        </button>

        {showCalendar && (
          <div className="absolute top-full left-0 mt-2 w-full max-w-xs bg-white rounded-lg shadow-xl border border-slate-200 p-3 z-30">
            {/* Month/Year Header */}
            <div className="flex items-center justify-between mb-3">
              <button
                type="button"
                onClick={goToPreviousMonth}
                className="p-1 hover:bg-slate-100 rounded transition-colors"
              >
                <ChevronLeft className="w-4 h-4 text-slate-600" />
              </button>
              
              <div className="text-center">
                <p className="text-xs font-bold text-slate-800 font-montserrat">
                  {monthNames[currentMonth]} {currentYear}
                </p>
              </div>
              
              <button
                type="button"
                onClick={goToNextMonth}
                className="p-1 hover:bg-slate-100 rounded transition-colors"
              >
                <ChevronRight className="w-4 h-4 text-slate-600" />
              </button>
            </div>

            {/* Info Banner */}
            <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-[10px] text-blue-700 text-center font-montserrat">
                Puedes seleccionar desde 3 días atrás hasta 2 días adelante
              </p>
            </div>

            {/* Day Names */}
            <div className="grid grid-cols-7 gap-0.5 mb-1">
              {dayNames.map((day) => (
                <div key={day} className="text-center text-[10px] font-semibold text-slate-500 py-1 font-montserrat">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-0.5">
              {calendarDays.map((day, index) => {
                const isSelected = isSelectedDate(day);
                const inRange = day ? isDateInRange(day, currentMonth, currentYear) : false;
                const isTodayDate = day ? isToday(day, currentMonth, currentYear) : false;
                
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleDateSelect(day)}
                    disabled={!day || !inRange}
                    className={`
                      aspect-square rounded text-xs transition-all font-montserrat relative
                      ${!day ? 'invisible' : ''}
                      ${!inRange && day ? 'text-slate-300 cursor-not-allowed' : ''}
                      ${isSelected ? 'bg-brand-primary text-white font-bold hover:bg-brand-primary/90' : ''}
                      ${!isSelected && inRange ? 'text-slate-700 hover:bg-brand-primary/10' : ''}
                      ${isTodayDate && !isSelected ? 'ring-2 ring-brand-gold font-bold' : ''}
                    `}
                  >
                    {day}
                    {isTodayDate && (
                      <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 bg-brand-gold rounded-full"></span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-center gap-3 text-[10px] text-slate-600">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded border-2 border-brand-gold"></div>
                <span>Hoy</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-brand-primary"></div>
                <span>Seleccionado</span>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-3 pt-3 border-t border-slate-200">
              <button
                type="button"
                onClick={onToggleCalendar}
                className="w-full py-1.5 text-xs text-slate-600 hover:text-slate-800 transition-colors font-montserrat"
              >
                Cerrar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RestrictedDateSelector;